'use client'

import React from 'react'
import {cn} from './cn'

interface HistoryPaginationProps {
    page: number
    totalPages: number
    onPageChange: (page: number) => void
    loading?: boolean
    className?: string
    previousLabel?: string
    nextLabel?: string
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const HistoryPagination: React.FC<HistoryPaginationProps> = ({
    page,
    totalPages,
    onPageChange,
    loading = false,
    className,
    previousLabel = 'Previous',
    nextLabel = 'Next'
}) => {
    const safeTotal = Number.isFinite(totalPages) ? Math.max(1, Math.floor(totalPages)) : 1
    if (safeTotal <= 1) return null

    const safePage = Number.isFinite(page) ? clamp(Math.floor(page), 0, safeTotal - 1) : 0
    const hasPrev = safePage > 0
    const hasNext = safePage < safeTotal - 1

    const goPrev = () => {
        if (!loading && hasPrev) onPageChange(safePage - 1)
    }

    const goNext = () => {
        if (!loading && hasNext) onPageChange(safePage + 1)
    }

    return (
        <div className={cn('mt-3 flex items-center justify-center gap-3 text-xs text-gray-600 dark:text-gray-400', className)}>
            <button
                type="button"
                onClick={goPrev}
                disabled={!hasPrev || loading}
                aria-label={previousLabel}
                className={cn(
                    'inline-flex h-7 w-7 admin-compact:h-11 admin-compact:w-11 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition-colors dark:border-gray-700 dark:text-gray-300',
                    hasPrev && !loading
                        ? 'hover:border-gray-300 hover:text-gray-900 dark:hover:border-gray-500 dark:hover:text-gray-100'
                        : 'opacity-40 cursor-not-allowed'
                )}
            >
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3 w-3">
                    <path
                        d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                        clipRule="evenodd"
                        fillRule="evenodd"
                    />
                </svg>
            </button>
            <span className="min-w-[4rem] text-center">
                {safePage + 1} / {safeTotal}
            </span>
            <button
                type="button"
                onClick={goNext}
                disabled={!hasNext || loading}
                aria-label={nextLabel}
                className={cn(
                    'inline-flex h-7 w-7 admin-compact:h-11 admin-compact:w-11 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition-colors dark:border-gray-700 dark:text-gray-300',
                    hasNext && !loading
                        ? 'hover:border-gray-300 hover:text-gray-900 dark:hover:border-gray-500 dark:hover:text-gray-100'
                        : 'opacity-40 cursor-not-allowed'
                )}
            >
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3 w-3">
                    <path
                        d="M7.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L10.94 10 7.22 6.28a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                        fillRule="evenodd"
                    />
                </svg>
            </button>
        </div>
    )
}

HistoryPagination.displayName = 'HistoryPagination'

export type {HistoryPaginationProps}
